import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom-v5-compat';
import { Card, Button, Space, Spin, Alert, Typography, Descriptions, message, Divider } from 'antd';
import { CheckOutlined, FileOutlined } from '@ant-design/icons';
import api from '../api';
import { useTranslation } from 'react-i18next';
import useCurrentUser from '../helpers/useCurrentUser';
import useCurrentOrg from '../helpers/useCurrentOrg';
import { useOrgPath } from '../helpers/OrgNavLink';
import { setAfterLogin } from '../helpers/useAfterLogin';
import { markdown } from 'markdown';
import { Parser } from 'html-to-react';

const { Title, Paragraph } = Typography;

const AccessGrantAgreement = () => {
    const { t } = useTranslation();
    const { accessRequestId, guestCode } = useParams();
    const navigate = useNavigate();
    const getPath = useOrgPath();
    const currentUser = useCurrentUser();
    const currentOrg = useCurrentOrg();
    
    const [loading, setLoading] = useState(true);
    const [grant, setGrant] = useState(null);
    const [grantId, setGrantId] = useState(null);
    const [error, setError] = useState(null);
    const [agreeing, setAgreeing] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    
    useEffect(() => {
        // Prevent multiple API calls
        if (!accessRequestId || hasFetched) return;
        
        // Check if user is authenticated
        if (!currentUser && !guestCode) {
            // Set the current URL as the after-login destination
            setAfterLogin(window.location.pathname);
            
            // Show a message and redirect to login
            message.warning(t('grant-agreement-requires-login', 'Please sign in or sign up with the email address associated with this grant to continue.'));
            navigate(getPath('/login'));
            return;
        }
        
        // Mark as fetched to prevent duplicate calls
        setHasFetched(true);
        
        // Load the grant details
        api.get(`/api/access_requests/find?id=${accessRequestId}`).then(res => {
            const accessRequest = res.data.access_request;
            
            if (!accessRequest.is_grant) {
                setError(t('error-not-a-grant', 'This is not a grant access request.'));
                setLoading(false);
                return;
            }
            
            setGrant(accessRequest);
            setGrantId(accessRequest.id);
            setLoading(false);
            
            // If already agreed, redirect to the grant page (without /agree suffix)
            if (accessRequest.agreed_at) {
                message.info(t('grant-already-agreed', 'You have already agreed to this grant.'));
                // Redirect to the grant detail page, not the agree page
                // Use the ID from the URL, not the slug, to avoid path issues
                window.location.href = getPath(`/access-grants/${accessRequestId}`);
            }
        }).catch(err => {
            setError(t('error-grant-not-found', 'Grant not found or you do not have access.'));
            setLoading(false);
        });
    }, [accessRequestId, guestCode]); // Reduce dependencies to prevent infinite loops
    
    const handleAgree = () => {
        if (!currentUser) {
            setError(t('error-must-be-logged-in', 'You must be logged in to agree to this grant.'));
            return;
        }
        
        setAgreeing(true);
        
        // Use the accessRequestId or grant.code for the agree endpoint
        const grantIdentifier = grant?.code || accessRequestId;
        
        // The agree endpoint is outside /api scope but we can still use api utility
        // by providing the full path starting with /
        api.post(`/access-requests/${grantIdentifier}/agree`, { guest_code: guestCode }).then(res => {
            message.success(t('message-grant-agreed', 'You have successfully agreed to the grant terms.'));
            // Redirect to the grant detail page using window.location to ensure clean navigation
            // Use the accessRequestId from the URL to maintain consistency
            window.location.href = getPath(`/access-grants/${accessRequestId}`);
        }).catch(err => {
            const errorMsg = err.response?.data?.error || t('error-agreeing-to-grant', 'Error agreeing to grant. Please try again.');
            setError(errorMsg);
            setAgreeing(false);
        });
    };
    
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }
    
    if (error) {
        return (
            <div style={{ maxWidth: '600px', margin: '50px auto' }}>
                <Alert message={error} type="error" />
            </div>
        );
    }
    
    return (
        <div style={{ maxWidth: '800px', margin: '50px auto', padding: '20px' }}>
            <Card>
                <Title level={2}>{t('access-grant-agreement', 'Access Grant Agreement')}</Title>
                
                <Paragraph>
                    {t('grant-agreement-intro', 'You have been granted access to the following assets:')}
                </Paragraph>
                
                <Card type="inner" style={{ marginBottom: '20px' }}>
                    <Title level={4}>{grant.name}</Title>
                    {grant.description && <Paragraph>{grant.description}</Paragraph>}
                    
                    <Descriptions bordered size="small" style={{ marginTop: '16px' }}>
                        <Descriptions.Item label={t('granted-by', 'Granted By')} span={3}>
                            {grant.finalized_by?.name || t('unknown', 'Unknown')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('number-of-assets', 'Number of Assets')} span={3}>
                            <FileOutlined /> {grant.granted_assets_count || 0} {t('assets', 'assets')}
                        </Descriptions.Item>
                        {grant.valid_through && (
                            <Descriptions.Item label={t('valid-until', 'Valid Until')} span={3}>
                                {new Date(grant.valid_through).toLocaleDateString()}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={t('permissions', 'Permissions')} span={3}>
                            <div>
                                {t('images-and-video', 'Images & Video')}: {t(grant.granted_image_and_video_permission || 'none', grant.granted_image_and_video_permission || 'None')}
                            </div>
                            <div>
                                {t('other-files', 'Other Files')}: {t(grant.granted_other_permission || 'none', grant.granted_other_permission || 'None')}
                            </div>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
                
                {currentOrg?.access_request_terms && (
                    <>
                        <Divider>{t('terms-of-use', 'Terms of Use')}</Divider>
                        <Card type="inner" style={{ marginBottom: '20px', backgroundColor: '#f5f5f5' }}>
                            <div className="markdown-content" style={{ whiteSpace: 'pre-wrap' }}>
                                {(new Parser()).parse(markdown.toHTML(currentOrg.access_request_terms))}
                            </div>
                        </Card>
                    </>
                )}
                
                <Alert
                    message={t('grant-agreement-notice', 'Important Notice')}
                    description={t('grant-agreement-terms', 'By clicking "I Agree" below, you acknowledge that you have read and agree to the terms of use for these assets as specified by the organization.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: '20px' }}
                />
                
                <Space>
                    <Button 
                        type="primary" 
                        icon={<CheckOutlined />} 
                        onClick={handleAgree}
                        loading={agreeing}
                        size="large"
                        disabled={!currentUser}
                    >
                        {t('button-i-agree', 'I Agree')}
                    </Button>
                    <Button onClick={() => navigate('/')} size="large">
                        {t('button-cancel', 'Cancel')}
                    </Button>
                </Space>
                
                {!currentUser && (
                    <Alert
                        message={t('login-required', 'Login Required')}
                        description={t('login-to-agree', 'Please log in to agree to this grant.')}
                        type="warning"
                        style={{ marginTop: '20px' }}
                    />
                )}
            </Card>
        </div>
    );
};

export default AccessGrantAgreement;